import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import placesApi from "@/api/placesApi";
import propertiesApi from "@/api/propertiesApi";
import MountainRidge from "@/components/MountainRidge/MountainRidge";
import PropertiesMap from "@/components/PropertiesMap/PropertiesMap";
import PropertyCard from "@/components/PropertyCard/PropertyCard";
import PropertyFiltersPanel, {
  EMPTY_FILTERS,
} from "@/components/PropertyFiltersPanel/PropertyFiltersPanel";
import SearchBar from "@/components/SearchBar/SearchBar";
import PROPERTY_TYPES from "@/constants/PropertyTypes";
import MapBounds from "@/model/MapBounds";
import PlaceResponse from "@/model/PlaceResponse";
import PropertyFilters from "@/model/PropertyFilters";
import PropertyResponse from "@/model/PropertyResponse";
import PropertySearchQuery from "@/model/PropertySearchQuery";
import SortOrder from "@/model/SortOrder";
import StaySearch from "@/model/StaySearch";
import guestsUtils from "@/utils/guests";
import { computeNights } from "@/utils/pricing";
import "@/pages/HomePage/HomePage.scss";

const FILTER_DEBOUNCE_MS = 300;

function numberOrUndefined(value: string): number | undefined {
  return value.trim() === "" || Number.isNaN(Number(value))
    ? undefined
    : Number(value);
}

function sortProperties(
  properties: PropertyResponse[],
  sortOrder: SortOrder
): PropertyResponse[] {
  const price = (property: PropertyResponse) =>
    property.nightlyPriceAmount ?? 0;
  if (sortOrder === "price-asc") {
    return [...properties].sort((a, b) => price(a) - price(b));
  }
  if (sortOrder === "price-desc") {
    return [...properties].sort((a, b) => price(b) - price(a));
  }
  return properties;
}

function HomePage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // The search lives in the URL so the back button and shared links restore it.
  const search: StaySearch = {
    destination: searchParams.get("destination") ?? "",
    checkIn: searchParams.get("checkIn") ?? "",
    checkOut: searchParams.get("checkOut") ?? "",
    guests: guestsUtils.fromSearchParams(searchParams),
  };
  const placeId = searchParams.get("place") ?? "";

  const [places, setPlaces] = useState<PlaceResponse[]>([]);

  const [filters, setFilters] = useState<PropertyFilters>(EMPTY_FILTERS);
  const [sortOrder, setSortOrder] = useState<SortOrder>("recommended");
  const [searchAsMapMoves, setSearchAsMapMoves] = useState(false);
  const [bounds, setBounds] = useState<MapBounds | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const [properties, setProperties] = useState<PropertyResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const query: PropertySearchQuery = {
    // Searching by map area replaces the destination text.
    city: searchAsMapMoves ? undefined : search.destination || undefined,
    placeId: searchAsMapMoves ? undefined : placeId || undefined,
    bounds: searchAsMapMoves && bounds ? bounds : undefined,
    checkIn: search.checkIn || undefined,
    checkOut: search.checkOut || undefined,
    guests: search.guests ? guestsUtils.countedGuests(search.guests) : undefined,
    propertyType: filters.propertyType || undefined,
    minBedrooms: numberOrUndefined(filters.minBedrooms),
    minPrice: numberOrUndefined(filters.minPrice),
    maxPrice: numberOrUndefined(filters.maxPrice),
    amenities: filters.amenities,
  };
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      setIsLoading(true);
      setError(false);

      propertiesApi
        .searchProperties(JSON.parse(queryKey) as PropertySearchQuery)
        .then((result) => {
          if (!cancelled) {
            setProperties(result.content ?? []);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setError(true);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, FILTER_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [queryKey]);

  useEffect(() => {
    let cancelled = false;
    // The quick-pick row is optional: without places (or if they fail to load) it just stays hidden.
    placesApi
      .listPlaces()
      .then((result) => {
        if (!cancelled) {
          setPlaces(result.content ?? []);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  const results = useMemo(
    () => sortProperties(properties, sortOrder),
    [properties, sortOrder]
  );
  const nights = computeNights(search.checkIn, search.checkOut);

  const linkParams = new URLSearchParams();
  if (search.checkIn && search.checkOut) {
    linkParams.set("checkIn", search.checkIn);
    linkParams.set("checkOut", search.checkOut);
  }
  guestsUtils.setSearchParams(linkParams, search.guests);
  const linkSearch = linkParams.toString();

  // Typing a destination replaces the picked place; otherwise the place is kept.
  const handleSearch = (
    next: StaySearch,
    nextPlaceId = next.destination.trim() ? "" : placeId
  ) => {
    const params = new URLSearchParams();
    (["destination", "checkIn", "checkOut"] as const).forEach((key) => {
      if (next[key].trim()) {
        params.set(key, next[key].trim());
      }
    });
    guestsUtils.setSearchParams(params, next.guests);
    if (nextPlaceId) {
      params.set("place", nextPlaceId);
    }
    setSearchAsMapMoves(false);
    setSearchParams(params);
  };

  const handlePlaceClick = (id: string) =>
    handleSearch({ ...search, destination: "" }, id === placeId ? "" : id);

  const activeFilterCount =
    (filters.minBedrooms ? 1 : 0) +
    (filters.minPrice || filters.maxPrice ? 1 : 0) +
    filters.amenities.length;

  return (
    <div className="home-page">
      <section className="home-page__hero">
        <div className="home-page__hero-text">
          <p className="eyebrow">{t("home.hero.eyebrow")}</p>
          <h1>{t("home.hero.title")}</h1>
          <p>{t("home.hero.subtitle")}</p>
        </div>

        <SearchBar
          key={searchParams.toString()}
          initialValue={search}
          onSearch={handleSearch}
        />

        {places.length > 0 && (
          <div className="home-page__destinations">
            <span>{t("home.hero.popular")}</span>
            {places
              .filter(
                (place): place is PlaceResponse & { id: string } =>
                  Boolean(place.id)
              )
              .map((place) => (
                <button
                  key={place.id}
                  type="button"
                  className={`home-page__destination${
                    place.id === placeId ? " home-page__destination--active" : ""
                  }`}
                  aria-pressed={place.id === placeId}
                  onClick={() => handlePlaceClick(place.id)}
                >
                  {place.name}
                </button>
              ))}
          </div>
        )}

        <MountainRidge className="home-page__ridge" />
      </section>

      <section className="home-page__listings">
        <div className="home-page__toolbar">
          <div
            className="home-page__categories"
            role="group"
            aria-label={t("filters.propertyTypeLabel")}
          >
            {(["", ...PROPERTY_TYPES] as const).map((type) => (
              <button
                key={type || "all"}
                type="button"
                aria-pressed={filters.propertyType === type}
                className={`home-page__category${
                  filters.propertyType === type
                    ? " home-page__category--active"
                    : ""
                }`}
                onClick={() => setFilters({ ...filters, propertyType: type })}
              >
                {type
                  ? t(`property.typePlural.${type}`)
                  : t("filters.allTypes")}
              </button>
            ))}
          </div>

          <div className="home-page__tools">
            <button
              type="button"
              className="button-ghost home-page__filters-toggle"
              aria-expanded={showFilters}
              onClick={() => setShowFilters((current) => !current)}
            >
              {t("filters.title")}
              {activeFilterCount > 0 && (
                <span className="home-page__filter-count">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <label className="home-page__sort">
              <span>{t("home.results.sortLabel")}</span>
              <select
                value={sortOrder}
                onChange={(event) =>
                  setSortOrder(event.target.value as SortOrder)
                }
              >
                <option value="recommended">
                  {t("home.results.sortRecommended")}
                </option>
                <option value="price-asc">
                  {t("home.results.sortPriceAsc")}
                </option>
                <option value="price-desc">
                  {t("home.results.sortPriceDesc")}
                </option>
              </select>
            </label>
          </div>
        </div>

        {showFilters && (
          <PropertyFiltersPanel filters={filters} onChange={setFilters} />
        )}

        <div className="home-page__content">
          <div className="home-page__results">
            <h2 className="home-page__count">
              {isLoading
                ? t("home.results.loading")
                : t("home.results.count", { count: results.length })}
            </h2>

            {!isLoading && error && (
              <p role="alert" className="home-page__empty">
                {t("home.results.error")}
              </p>
            )}

            {!isLoading && !error && results.length === 0 && (
              <p className="home-page__empty">{t("home.results.empty")}</p>
            )}

            {!error && results.length > 0 && (
              <div className="home-page__grid">
                {results.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    linkSearch={linkSearch}
                    nights={nights}
                    highlighted={property.id === hoveredId}
                    onHoverChange={setHoveredId}
                  />
                ))}
              </div>
            )}
          </div>

          <aside className="home-page__map">
            <PropertiesMap
              properties={results}
              highlightedId={hoveredId}
              linkSearch={linkSearch}
              followsUser={searchAsMapMoves}
              onBoundsChange={searchAsMapMoves ? setBounds : undefined}
            />
            <label className="home-page__map-toggle">
              <input
                type="checkbox"
                checked={searchAsMapMoves}
                onChange={(event) => {
                  setSearchAsMapMoves(event.target.checked);
                  setBounds(null);
                }}
              />
              {t("home.map.searchAsMapMoves")}
            </label>
          </aside>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
